import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="inbox-demo"
//
// Drives the marketing hero's animated inbox as a faithful mini-simulation of a
// handoff: the flow collects variables, hands the thread to an agent (the
// assignment and bot state flip), and the agent composes replies in the
// composer before each one lands. Purely presentational, so it honours
// reduced-motion by showing the finished conversation at rest.
export default class extends Controller {
  static targets = [
    "step", "incoming", "fit", "scale",
    "composer", "box", "hint", "replyTab", "noteTab", "assignPanel", "assignPill", "botPill", "variables",
  ];

  // The workspace is laid out at a fixed design width and scaled down to fill
  // whatever column it lands in, the way a screenshot would.
  static DESIGN_WIDTH = 1160;

  static REPLY_HINT = '<svg viewBox="0 0 24 24"><path d="M9 10l-4 4 4 4"/><path d="M5 14h11a4 4 0 004-4V6"/></svg>Cmd or Ctrl + Enter to send';
  static NOTE_HINT = '<svg viewBox="0 0 24 24"><path d="M3 3l18 18"/><path d="M10.6 6.1A9 9 0 0121 12a9 9 0 01-2.4 3.1M6.6 6.7A9 9 0 003 12a9 9 0 0013.2 5.5"/></svg>Only agents see notes';

  connect() {
    this.fit = this.fit.bind(this);
    this.fit();
    window.addEventListener("resize", this.fit);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.fit());

    this.timers = [];
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.showFinished();
      return;
    }
    this.run();
  }

  disconnect() {
    (this.timers || []).forEach(clearTimeout);
    window.removeEventListener("resize", this.fit);
  }

  fit() {
    if (!this.hasFitTarget || !this.hasScaleTarget) return;
    const s = this.fitTarget.clientWidth / this.constructor.DESIGN_WIDTH;
    this.scaleTarget.style.transform = `scale(${s})`;
    this.fitTarget.style.height = `${this.scaleTarget.offsetHeight * s}px`;
  }

  at(ms, fn) { this.timers.push(setTimeout(fn, ms)); }

  run() {
    this.timers.forEach(clearTimeout);
    this.timers = [];
    this.reset();

    // Measure the whole run first, so the new conversation drops into the queue
    // about a third of the way through rather than at the very start.
    let total = 700;
    this.stepTargets.forEach((el) => {
      if (el.dataset.compose) total += el.dataset.compose.length * 18 + 700;
      total += Number(el.dataset.gap || 1100);
    });
    total += 2800;
    this.at(Math.round(total / 3), () => this.incomingTargets.forEach((el) => el.classList.add("is-in")));

    let t = 700;
    this.stepTargets.forEach((el) => {
      const compose = el.dataset.compose;
      if (compose) {
        this.at(t, () => this.type(compose, el.dataset.mode));
        t += compose.length * 18 + 700; // typewriter, then a beat before it sends
        this.at(t, () => { this.clearComposer(); el.classList.add("is-in"); this.effects(el); });
      } else {
        this.at(t, () => { el.classList.add("is-in"); this.effects(el); });
      }
      t += Number(el.dataset.gap || 1100);
    });

    this.at(t + 2800, () => this.run());
  }

  effects(el) {
    // The claim lands a beat after the note posts, the way it does when an
    // agent takes over: the note goes up, then the assignment fills in.
    if (el.dataset.assign === "true") this.at(600, () => this.claim());
    if (el.dataset.var) this.addVar(el.dataset.var);
  }

  // The agent claims the conversation: assignment fills in and the bot steps
  // aside, exactly as it does when a person first replies.
  claim() {
    if (this.hasAssignPanelTarget) {
      this.assignPanelTarget.innerHTML =
        '<div class="idemo-aname">Jordan Lee</div><div class="idemo-asrc">manual</div>';
    }
    if (this.hasAssignPillTarget) {
      this.assignPillTarget.textContent = "Jordan Lee";
      this.assignPillTarget.classList.add("assign");
    }
    if (this.hasBotPillTarget) this.botPillTarget.textContent = "Bot paused";
  }

  addVar(spec) {
    if (!this.hasVariablesTarget) return;
    const [k, v] = spec.split("|");
    if (this.variablesTarget.dataset.filled !== "true") {
      this.variablesTarget.innerHTML = "";
      this.variablesTarget.dataset.filled = "true";
    }
    const row = document.createElement("div");
    row.className = "idemo-krow idemo-varrow";
    row.innerHTML = `<span class="k">${k}</span><span class="v">${v}</span>`;
    this.variablesTarget.appendChild(row);
  }

  type(text, mode) {
    if (!this.hasComposerTarget) return;
    const id = (this.typeId = (this.typeId || 0) + 1);
    this.setMode(mode);
    this.composerTarget.classList.add("has-text");
    let i = 0;
    const tick = () => {
      if (id !== this.typeId) return; // superseded by a clear
      this.composerTarget.textContent = text.slice(0, ++i);
      if (i < text.length) this.timers.push(setTimeout(tick, 18));
    };
    tick();
  }

  // Reply by default; a note switches the composer to its own tab, tint, hint
  // and placeholder, the way the inbox does.
  setMode(mode) {
    const note = mode === "note";
    if (this.hasReplyTabTarget) this.replyTabTarget.classList.toggle("on", !note);
    if (this.hasNoteTabTarget) this.noteTabTarget.classList.toggle("on", note);
    if (this.hasBoxTarget) this.boxTarget.classList.toggle("note-mode", note);
    if (this.hasHintTarget) {
      this.hintTarget.innerHTML = note ? this.constructor.NOTE_HINT : this.constructor.REPLY_HINT;
    }
    this.placeholder = note ? "Write an internal note" : "Write a reply";
  }

  clearComposer() {
    if (!this.hasComposerTarget) return;
    this.typeId = (this.typeId || 0) + 1; // stop any running typewriter
    this.setMode(null);
    this.composerTarget.classList.remove("has-text");
    this.composerTarget.textContent = this.placeholder;
  }

  reset() {
    this.stepTargets.forEach((el) => el.classList.remove("is-in"));
    this.incomingTargets.forEach((el) => el.classList.remove("is-in"));
    this.clearComposer();
    if (this.hasAssignPanelTarget) this.assignPanelTarget.innerHTML = '<div class="idemo-asrc">Unassigned</div>';
    if (this.hasAssignPillTarget) {
      this.assignPillTarget.textContent = "Unassigned";
      this.assignPillTarget.classList.remove("assign");
    }
    if (this.hasBotPillTarget) this.botPillTarget.textContent = "Bot active";
    if (this.hasVariablesTarget) {
      this.variablesTarget.innerHTML = '<div class="idemo-krow"><span class="k">No variables collected</span></div>';
      this.variablesTarget.dataset.filled = "false";
    }
  }

  showFinished() {
    this.stepTargets.forEach((el) => el.classList.add("is-in"));
    this.incomingTargets.forEach((el) => el.classList.add("is-in"));
    this.claim();
    this.stepTargets.forEach((el) => { if (el.dataset.var) this.addVar(el.dataset.var); });
  }
}
